import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Link as MuiLink,
  TextField,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";

import Button, { Color } from "../../../../../components/Form/Button";
import { useSecretManager } from "../../../../../hooks/useSecretManager";
import env from "../../../../../util/env";
import { SIGNUP_COLORS, commonStyles } from "../../tokens";

type SixthStepProps = {
  isLoading?: boolean;
  objectPreview?: any;
  onPreviousStep?: () => void;
  onSubmit: (data: {
    ownWords: string;
    newsLetter: boolean;
    termsAccepted: boolean;
    captchaToken?: string | null;
  }) => void;
  recaptchaSiteKey?: string;
};

const SixthStep: React.FC<SixthStepProps> = ({
  isLoading,
  objectPreview,
  onPreviousStep,
  onSubmit,
}) => {
  const { t: translate } = useTranslation();
  const { secret } = useSecretManager(env.SECRET_ID);

  const [ownWords, setOwnWords] = useState<string>(objectPreview?.ownWords ?? "");
  const [touched, setTouched] = useState<boolean>(false);
  const [newsLetter, setNewsLetter] = useState<boolean>(!!objectPreview?.newsLetter);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaExpired, setCaptchaExpired] = useState<boolean>(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const minChars = 50;
  const maxChars = 500;
  const chars = ownWords.trim().length;
  const tooShort = touched && chars < minChars;
  const tooLong = chars > maxChars;

  const siteKey = useMemo<string>(
    () => ((secret as any)?.CAPTCHA_API_KEY as string) || "",
    [secret]
  );

  const isValid =
    !tooLong && chars >= minChars && termsAccepted && (siteKey ? !!captchaToken : true);

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;

    onSubmit({
      ownWords: ownWords.trim(),
      newsLetter,
      termsAccepted,
      captchaToken: captchaToken ?? undefined,
    });
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography
          fontFamily="Manrope"
          fontWeight={600}
          fontSize="16px"
          lineHeight="24px"
          color={tooShort ? SIGNUP_COLORS.error : SIGNUP_COLORS.primary}
          mb={1}
        >
          {translate(
            "signup.right.ownWords.questionLabel",
            "What are your challenges or obstacles right now?"
          )}
          <Typography component="span" color={SIGNUP_COLORS.error} ml={0.5}>
            *
          </Typography>
        </Typography>

        <TextField
          data-cy="ownWordsTextarea"
          aria-label="ownWordsTextarea"
          value={ownWords}
          onChange={(e) => {
            const v = e.target.value.slice(0, maxChars);
            setOwnWords(v);
          }}
          onBlur={() => setTouched(true)}
          multiline
          minRows={5}
          fullWidth
          placeholder={translate("signup.right.ownWords.placeholder", "Type something...")}
          error={tooShort || tooLong}
          helperText={" "}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              fontFamily: "Manrope",
              fontSize: "16px",
              lineHeight: "24px",
              color: SIGNUP_COLORS.gray550,
            },
            "& .MuiFormHelperText-root": {
              fontFamily: "Manrope",
              letterSpacing: "0.2px",
            },
          }}
        />

        <Box mt={0.5}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              lineHeight: "20px",
              color: SIGNUP_COLORS.gray600,
            }}
          >
            {`${Math.min(chars, maxChars)} / ${maxChars} ${translate(
              "signup.right.ownWords.characters",
              "characters"
            )}`}
          </Typography>

          {tooShort && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: SIGNUP_COLORS.error,
                fontFamily: "Manrope",
              }}
            >
              {translate(
                "signup.right.ownWords.helperShort",
                "Your message should be at least 50 characters."
              )}
            </Typography>
          )}

          {tooLong && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: SIGNUP_COLORS.error,
                fontFamily: "Manrope",
              }}
            >
              {translate(
                "signup.right.ownWords.helperLong",
                "Please keep your message under 500 characters."
              )}
            </Typography>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} mt={3}>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', mt: 0.5 }}
          control={
            <Checkbox
              checked={newsLetter}
              onChange={(e) => setNewsLetter(e.target.checked)}
              sx={{ mt: -0.5 }}
            />
          }
          label={
            <Typography
              fontFamily="Manrope"
              fontSize="16px"
              lineHeight="24px"
              letterSpacing="0.2px"
              fontWeight={400}
              color={SIGNUP_COLORS.gray550}
            >
              {translate(
                "signup.right.getEmailsFromShipeeziAboutProductUpdatesIndustryNewsAndEventsIfYouChangeYourMindYouCanUnsubscribeAtAnyTime"
              )}
            </Typography>
          }
        />
      </Grid>

      <Grid item xs={12} mt={3}>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', mt: 0.5 }}
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              sx={{ mt: -0.5 }}
            />
          }
          label={
            <Typography
              fontFamily="Manrope"
              fontSize="16px"
              lineHeight="24px"
              letterSpacing="0.2px"
              fontWeight={400}
              color={SIGNUP_COLORS.gray550}
            >
              {translate("signup.right.termsPrefix", "By continuing, you're agreeing to Shipeezi's")}&nbsp;
              <MuiLink
                href="https://shipeezi.com/legal"
                target="_blank"
                underline="hover"
                sx={{ color: SIGNUP_COLORS.primary, fontWeight: 600 }}
              >
                {translate("signup.right.termsOfSale", "Terms of Sale")}
              </MuiLink>
              ,{" "}
              <MuiLink
                href="https://shipeezi.com/legal"
                target="_blank"
                underline="hover"
                sx={{ color: SIGNUP_COLORS.primary, fontWeight: 600 }}
              >
                {translate("signup.right.termsOfUse", "Terms of Use")}
              </MuiLink>
              ,{" "}
              <MuiLink
                href="https://shipeezi.com/legal"
                target="_blank"
                underline="hover"
                sx={{ color: SIGNUP_COLORS.primary, fontWeight: 600 }}
              >
                {translate("signup.right.privacyAndCompliance", "Privacy & Compliance Policy")}
              </MuiLink>
              ,{" "}
              <MuiLink
                href="https://shipeezi.com/legal"
                target="_blank"
                underline="hover"
                sx={{ color: SIGNUP_COLORS.primary, fontWeight: 600 }}
              >
                {translate("signup.right.saasAgreement", "SAAS Agreement and SLA")}
              </MuiLink>
              {" "}{translate("signup.right.and", "and")}{" "}
              <MuiLink
                href="https://shipeezi.com/legal"
                target="_blank"
                underline="hover"
                sx={{ color: SIGNUP_COLORS.primary, fontWeight: 600 }}
              >
                {translate("signup.right.paymentTerms", "Payment Terms")}
              </MuiLink>
              .
            </Typography>
          }
        />
      </Grid>

      <Grid item xs={12} mt={3} sx={{ mb: 0 }}>
        {siteKey ? (
          <Box>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={siteKey}
              onChange={(token) => {
                setCaptchaExpired(false);
                setCaptchaToken(token);
              }}
              onExpired={() => {
                setCaptchaExpired(true);
                setCaptchaToken(null);
              }}
              theme="light"
            />
            {captchaExpired && (
              <Typography
                variant="body2"
                sx={{
                  display: "block",
                  mt: 1,
                  color: SIGNUP_COLORS.error,
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  lineHeight: "20px",
                }}
              >
                {translate("signup.right.captchaExpired", "Captcha expired")}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: SIGNUP_COLORS.error,
              fontFamily: "Manrope",
              fontSize: "14px",
              lineHeight: "20px",
            }}
          >
            {"reCAPTCHA site key not configured."}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} container spacing={2} mt={3}>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              if (recaptchaRef.current) recaptchaRef.current.reset();
              setCaptchaToken(null);
              onPreviousStep?.();
            }}
            disabled={!!isLoading}
            data-cy="backButton"
            aria-label="backButton"
            sx={commonStyles.backButton}
          >
            {translate("signup.right.back", "Back")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            customColor={Color.SECONDARY_DARK}
            onClick={handleSubmit}
            disabled={!isValid || !!isLoading}
            data-cy="requestAccountButton"
            sx={{
              height: 40,
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {translate("signup.right.requestAccount", "Request account")}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default SixthStep;
